import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { TIME_FORMAT } from 'util/formatAgo';
import { IWeatherItem, ResortWeatherLocation } from 'api/resort/types';
import { NameDay } from 'components/resort/ResortWeatherRow/NameDay';
import { DistanceUnit, SnowUnit, TemperatureUnit } from 'components/blocks/Unit';
import { IconWeather } from 'components/resort/IconWeather';
import classNames from 'classnames';

import { ReactComponent as SvgWind } from 'public/assets/icons/wind.svg';
import { ReactComponent as SvgClouds } from 'public/assets/icons/clouds.svg';
import { mpsToKmh } from 'i18n/units';
import { DateTime } from 'luxon';
import styles from './styles.module.scss';

export interface IResortWeatherRow {
  title: string,
  updatedAt: string,
  weatherDescription: string,
  base: string,
  summit: string,
  wind: number,
  snow: number,
  uuid: number,
}

export interface IPropsResortWeatherRow {
  weatherItem: IWeatherItem;
  className: string;
  weatherLocation: ResortWeatherLocation;
  isHourly?: boolean;
}

export const ResortWeatherRowHeader = (
  { updatedAt }: { updatedAt?: string, summit: number, base: number },
) => {
  const { t } = useOTSTranslation('common');

  return (
    <thead className={styles.box}>
      <th className={styles.title}>
        {
        updatedAt ? (
          <>
            <span className={classNames(styles.date, styles.title)}><NameDay date={updatedAt} /></span>
          </>
        ) : null
      }
      </th>
      <th className={styles.base}>
        <span className={styles.h5}>
          {t('resort.skiReport.highLow')}
        </span>
      </th>
      <th className={classNames(styles.icon, styles.wind)} title={t('common.windSpeed')}>
        <SvgWind />
      </th>
      <th className={classNames(styles.icon, styles.snow)} title={t('common.forecastedSnow')}>
        <SvgClouds />
      </th>
    </thead>
  );
};

export const ResortWeatherRow = (
  {
    weatherItem, weatherLocation, className, isHourly,
  }: IPropsResortWeatherRow,
) => {
  const { t } = useOTSTranslation('common');

  const getFormattedHourlyTime = () => DateTime.fromISO(weatherItem.datetime, { setZone: true }).toFormat(
    TIME_FORMAT,
  );

  const weatherDetail = weatherLocation === ResortWeatherLocation.base ? weatherItem.base : weatherItem.summit;
  if (!weatherDetail) return (<></>);

  const TitleRow = () => (
    isHourly ? (
      <span className={classNames(styles.h4, styles.title)}>
        {getFormattedHourlyTime()}
      </span>
    ) : (
      <span className={classNames(styles.h4, styles.title)}><NameDay date={weatherItem.datetime} /></span>
    )
  );

  return (
    <tr className={classNames(styles.box, className)}>
      <th scope="row" className={styles.title}>
        <div className={styles.titleWrapper}>
          <TitleRow />
          <div className={styles.weatherDescription}>
            <IconWeather title={weatherDetail.type} />
          </div>
        </div>
      </th>
      <td className={styles.base}>
        <span className={styles.h4}>
          <TemperatureUnit
            values={[weatherDetail.temp.max]}
            type="temperature"
            removeEqual
          />
          {' '}
          /
          {' '}
          <TemperatureUnit
            values={[weatherDetail.temp.min]}
            type="temperature"
            separator="/"
            removeEqual
          />
        </span>
      </td>
      <td className={styles.wind} aria-label={t('common.windSpeed')}>
        <span className={styles.h4}><DistanceUnit values={mpsToKmh(weatherDetail.wind.speed)} type="kmhOrMileh" /></span>
      </td>
      <td className={styles.snow} aria-label={t('common.forecastedSnow')}>
        <span className={styles.h4}><SnowUnit values={weatherDetail.snow.snowfall} /></span>
      </td>
    </tr>
  );
};
